import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconPhoto } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { resolveImageSource } from "../../../../components/common/image";
import type { SaleDetailsDto, SaleOrderLineDto } from "../../../../api";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { resolveProductRouteFromVariant } from "../../../products/utils/resolve-product-route";
import { showErrorToast } from "../../../../utils/toasts";

function ItemRow({ line }: { line: SaleOrderLineDto }) {
  const imgSrc = resolveImageSource(line.image ?? null);
  const variantId = line.product?.id ?? null;
  const navigate = useNavigate();
  const [isNavigating, setIsNavigating] = useState(false);

  async function handleClick() {
    if (variantId == null || isNavigating) return;
    setIsNavigating(true);
    try {
      const href = await resolveProductRouteFromVariant(variantId, AppRoutes.ADMIN_PRODUCT_DETAILS);
      navigate(href);
    } catch {
      showErrorToast("Could not open product details.");
    } finally {
      setIsNavigating(false);
    }
  }

  const rowClasses = "flex items-center gap-4 max-md:gap-3 border-b border-borderLight py-3 last:border-0";
  const rowContent = (
    <>
      <div className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-borderDefault bg-bgLight">
        {imgSrc ? (
          <img src={imgSrc} alt={line.product?.name ?? line.name} className="size-full object-cover" />
        ) : (
          <IconPhoto size={20} className="text-textMuted" />
        )}
      </div>
      <Text variant="body" tone="dark" className="flex-[3] text-left max-md:min-w-0 max-md:break-words">{line.product?.name ?? line.name}</Text>
      <Text variant="body" tone="dark" className="w-16 text-center max-md:w-10">{line.quantity}</Text>
      <Text variant="body" tone="dark" className="flex-1 text-right max-md:hidden">{line.unitPrice.toLocaleString()}</Text>
      <Text variant="bodyBold" tone="dark" className="flex-1 text-right">{line.total.toLocaleString()}</Text>
    </>
  );

  return variantId != null ? (
    <button
      type="button"
      onClick={handleClick}
      disabled={isNavigating}
      className={`${rowClasses} w-full hover:bg-bgLight transition-colors ${isNavigating ? "opacity-60 cursor-wait" : ""}`}
    >
      {rowContent}
    </button>
  ) : (
    <div className={rowClasses}>{rowContent}</div>
  );
}

function SummaryRow({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="flex items-center gap-12 max-md:gap-6">
      <Text variant={bold ? "bodyBold" : "body"} tone={bold ? "dark" : "muted"}>{label}</Text>
      <Text variant={bold ? "bodyBold" : "body"} tone="dark" className="w-28 text-right">{value}</Text>
    </div>
  );
}

export function OrderItemsCard({ order }: { order: SaleDetailsDto }) {
  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-borderDefault bg-bgWhite px-6 py-5 max-md:px-4">
      <Heading variant="h3" tone="dark">Items</Heading>

      <div className="border-t border-borderLight pt-2">
        {/* Table header */}
        <div className="flex items-center gap-4 max-md:gap-3 border-b border-borderLight pb-3">
          <div className="w-12 shrink-0" />
          <Text variant="capsSm" tone="muted" className="flex-[3]">Product</Text>
          <Text variant="capsSm" tone="muted" className="w-16 text-center max-md:w-10">QTY</Text>
          <Text variant="capsSm" tone="muted" className="flex-1 text-right max-md:hidden">Unit Price (QAR)</Text>
          <Text variant="capsSm" tone="muted" className="flex-1 text-right">Total (QAR)</Text>
        </div>

        {(order.orderLines ?? []).map((line) => <ItemRow key={line.id} line={line} />)}

        {/* Summary */}
        <div className="flex flex-col items-end gap-2 border-t border-borderLight pt-4">
          <SummaryRow label="Subtotal" value={`QAR ${order.amountUntaxed.toLocaleString()}`} />
          <SummaryRow
            label={order.amountUntaxed > 0 && order.amountTax > 0
              ? `Tax (${Math.round((order.amountTax / order.amountUntaxed) * 100)}%)`
              : "Tax"}
            value={`QAR ${order.amountTax.toLocaleString()}`}
          />
          <SummaryRow label="Total" value={`QAR ${order.amountTotal.toLocaleString()}`} bold />
        </div>
      </div>
    </div>
  );
}
